import {
  ApiError,
  ClientConfig,
  createRequest,
  createCachedRequest,
  createTextDownload,
} from './client';
import * as T from './types';

function buildQueryString(params: Record<string, string | null | undefined>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    const normalized = value?.trim();
    if (normalized) {
      searchParams.set(key, normalized);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

export const createAuthService = (config: ClientConfig) => ({
  async me(): Promise<{ user: T.User }> {
    return createRequest<{ user: T.User }>('/api/auth/me', config);
  },
});

export const createLicenseService = (config: ClientConfig) => ({
  async redeem(code: string): Promise<T.LicenseAuthResponse> {
    return createRequest<T.LicenseAuthResponse>('/api/licenses/redeem', config, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
  async me(): Promise<{ entitlements: T.ActiveEntitlements }> {
    return createRequest<{ entitlements: T.ActiveEntitlements }>('/api/licenses/me', config);
  },
  async features(): Promise<{ features: string[] }> {
    return createRequest<{ features: string[] }>('/api/admin/licenses/features', config);
  },
  async listPlans(): Promise<{ plans: T.LicensePlan[] }> {
    return createRequest<{ plans: T.LicensePlan[] }>('/api/admin/licenses/plans', config);
  },
  async createPlan(data: {
    name: string;
    description?: string | null;
    examTypes: string[];
    languageIds?: string[];
    bankIds: string[];
    features: string[];
    validDays?: number | null;
    tier?: 'basic' | 'pro' | 'flagship';
    durationDays?: number | null;
    examScope?: string;
    price?: number | string;
    isPurchasable?: boolean;
  }): Promise<{ plan: T.LicensePlan }> {
    return createRequest<{ plan: T.LicensePlan }>('/api/admin/licenses/plans', config, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async updatePlan(
    planId: string,
    data: {
      name?: string;
      description?: string | null;
      examTypes?: string[];
      languageIds?: string[];
      bankIds?: string[];
      features?: string[];
      validDays?: number | null;
      tier?: 'basic' | 'pro' | 'flagship';
      durationDays?: number | null;
      examScope?: string;
      price?: number | string;
      isPurchasable?: boolean;
      status?: 'active' | 'disabled';
    }
  ): Promise<{ plan: T.LicensePlan }> {
    return createRequest<{ plan: T.LicensePlan }>(`/api/admin/licenses/plans/${planId}`, config, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  async disablePlan(planId: string, reason?: string): Promise<{ plan: T.LicensePlan }> {
    return createRequest<{ plan: T.LicensePlan }>(
      `/api/admin/licenses/plans/${planId}/disable`,
      config,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    );
  },
  async enablePlan(planId: string, reason?: string): Promise<{ plan: T.LicensePlan }> {
    return createRequest<{ plan: T.LicensePlan }>(
      `/api/admin/licenses/plans/${planId}/enable`,
      config,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    );
  },
  async listCodes(): Promise<{ codes: T.RedemptionCode[] }> {
    return createRequest<{ codes: T.RedemptionCode[] }>('/api/admin/licenses/codes', config);
  },
  async generateCodes(data: {
    planId: string;
    quantity: number;
    prefix?: string;
    expiresAt?: string | null;
  }): Promise<{ codes: T.RedemptionCode[] }> {
    return createRequest<{ codes: T.RedemptionCode[] }>(
      '/api/admin/licenses/codes/generate',
      config,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  // License grants
  async listGrants(
    params?: T.LicenseGrantListFilters & T.PaginationParams
  ): Promise<T.LicenseGrantListResponse> {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.userId) query.set('userId', params.userId);
      if (params.examType) query.set('examType', params.examType);
      if (params.page) query.set('page', String(params.page));
      if (params.limit) query.set('limit', String(params.limit));
    }
    const qs = query.toString();
    return createRequest<T.LicenseGrantListResponse>(
      `/api/admin/licenses/grants${qs ? `?${qs}` : ''}`,
      config
    );
  },
  async getGrant(grantId: string): Promise<{ grant: T.LicenseGrantDetail }> {
    return createRequest<{ grant: T.LicenseGrantDetail }>(
      `/api/admin/licenses/grants/${grantId}`,
      config
    );
  },
  async revokeGrant(grantId: string, reason: string): Promise<T.LicenseRevokeResponse> {
    return createRequest<T.LicenseRevokeResponse>(
      `/api/admin/licenses/grants/${grantId}/revoke`,
      config,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
  },
  getGrantsExportCsvUrl(params?: T.LicenseGrantListFilters): string {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.userId) query.set('userId', params.userId);
      if (params.examType) query.set('examType', params.examType);
    }
    const qs = query.toString();
    return `${config.baseUrl}/api/admin/licenses/grants/export.csv${qs ? `?${qs}` : ''}`;
  },
  /** Authenticated grants CSV export (Bearer). Prefer this over opening {@link getGrantsExportCsvUrl} in a tab. */
  async exportGrantsCsv(
    params?: T.LicenseGrantListFilters
  ): Promise<{ text: string; filename: string }> {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.userId) query.set('userId', params.userId);
      if (params.examType) query.set('examType', params.examType);
    }
    const qs = query.toString();
    return createTextDownload(
      `/api/admin/licenses/grants/export.csv${qs ? `?${qs}` : ''}`,
      config,
      {},
      `license-grants-${new Date().toISOString().slice(0, 10)}.csv`
    );
  },

  // Codes
  async listCodesPaginated(
    params?: T.LicenseCodeListFilters & T.PaginationParams
  ): Promise<T.LicenseCodeListResponse> {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.examType) query.set('examType', params.examType);
      if (params.page) query.set('page', String(params.page));
      if (params.limit) query.set('limit', String(params.limit));
    }
    const qs = query.toString();
    return createRequest<T.LicenseCodeListResponse>(
      `/api/admin/licenses/codes${qs ? `?${qs}` : ''}`,
      config
    );
  },
  async getCode(codeId: string): Promise<{ code: T.LicenseCodeDetail }> {
    return createRequest<{ code: T.LicenseCodeDetail }>(
      `/api/admin/licenses/codes/${codeId}`,
      config
    );
  },
  async disableCode(codeId: string, reason: string): Promise<T.LicenseDisableCodeResponse> {
    return createRequest<T.LicenseDisableCodeResponse>(
      `/api/admin/licenses/codes/${codeId}/disable`,
      config,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
  },
  getCodesExportCsvUrl(params?: T.LicenseCodeListFilters): string {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.examType) query.set('examType', params.examType);
    }
    const qs = query.toString();
    return `${config.baseUrl}/api/admin/licenses/codes/export.csv${qs ? `?${qs}` : ''}`;
  },
  /**
   * Authenticated codes CSV export (Bearer). Includes plaintext `code` when
   * stored; legacy hash-only rows leave that column empty.
   */
  async exportCodesCsv(
    params?: T.LicenseCodeListFilters
  ): Promise<{ text: string; filename: string }> {
    const query = new URLSearchParams();
    if (params) {
      if (params.status) query.set('status', params.status);
      if (params.planId) query.set('planId', params.planId);
      if (params.examType) query.set('examType', params.examType);
    }
    const qs = query.toString();
    return createTextDownload(
      `/api/admin/licenses/codes/export.csv${qs ? `?${qs}` : ''}`,
      config,
      {},
      `redemption-codes-${new Date().toISOString().slice(0, 10)}.csv`
    );
  },
  async generateBatch(
    data: T.LicenseBatchRedemptionRequest
  ): Promise<T.LicenseBatchRedemptionResponse> {
    return createRequest<T.LicenseBatchRedemptionResponse>(
      '/api/admin/licenses/codes/batch',
      config,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  // Audit
  async listAuditEvents(
    params?: T.LicenseAuditEventListFilters & T.PaginationParams
  ): Promise<T.LicenseAuditEventListResponse> {
    const query = new URLSearchParams();
    if (params) {
      if (params.action) query.set('action', params.action);
      if (params.targetType) query.set('targetType', params.targetType);
      if (params.targetId) query.set('targetId', params.targetId);
      if (params.actorId) query.set('actorId', params.actorId);
      if (params.from) query.set('from', params.from);
      if (params.to) query.set('to', params.to);
      if (params.page) query.set('page', String(params.page));
      if (params.limit) query.set('limit', String(params.limit));
    }
    const qs = query.toString();
    return createRequest<T.LicenseAuditEventListResponse>(
      `/api/admin/licenses/audit${qs ? `?${qs}` : ''}`,
      config
    );
  },

  async listQuotaPolicies(planId?: string): Promise<{ policies: T.QuotaPolicyRow[] }> {
    const query = new URLSearchParams();
    if (planId) query.set('planId', planId);
    const qs = query.toString();
    return createRequest<{ policies: T.QuotaPolicyRow[] }>(
      `/api/admin/licenses/quota-policies${qs ? `?${qs}` : ''}`,
      config
    );
  },

  async upsertQuotaPolicy(data: T.UpsertQuotaPolicyInput): Promise<{ policy: T.QuotaPolicyRow }> {
    return createRequest<{ policy: T.QuotaPolicyRow }>('/api/admin/licenses/quota-policies', config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
});

export const createBankService = (config: ClientConfig) => ({
  async list(): Promise<{ banks: T.QuestionBank[] }> {
    return createCachedRequest<{ banks: T.QuestionBank[] }>('/api/banks', config);
  },
  async get(id: string): Promise<{ bank: T.QuestionBank }> {
    return createCachedRequest<{ bank: T.QuestionBank }>(`/api/banks/${id}`, config);
  },
  async myAccessible(scope: T.LearnerAccessibleBankScope = {}): Promise<{
    banks: T.QuestionBank[];
    scope?: T.LearnerAccessibleBankScope | null;
  }> {
    const query = buildQueryString({
      languageId: scope.languageId,
      examType: scope.examType,
      section: scope.section ?? scope.sectionId,
    });

    return createCachedRequest<{
      banks: T.QuestionBank[];
      scope?: T.LearnerAccessibleBankScope | null;
    }>(`/api/banks/my/accessible${query}`, config);
  },
  async download(id: string, deviceId?: string): Promise<any> {
    const query = deviceId ? `?deviceId=${deviceId}` : '';
    return createRequest(`/api/banks/${id}/download${query}`, config);
  },
  async checkUpdate(id: string, currentVersion: string): Promise<any> {
    return createCachedRequest(`/api/banks/${id}/check-update?version=${currentVersion}`, config);
  },
  async sync(id: string, currentVersion: string): Promise<any> {
    return createRequest(`/api/banks/${id}/sync?version=${encodeURIComponent(currentVersion)}`, config);
  },
  async signAudio(objectName: string): Promise<{ url: string }> {
    return createRequest('/api/banks/media/sign-audio', config, {
      method: 'POST',
      body: JSON.stringify({ objectName }),
    });
  },
  async preview(id: string): Promise<any> {
    return createCachedRequest(`/api/banks/${id}/preview`, config);
  },
});

export const createAdminService = (config: ClientConfig) => ({
  // Dashboard
  async getDashboardStats(params?: T.DashboardStatsQuery): Promise<T.DashboardStats> {
    const query = buildQueryString({
      languageId: params?.languageId,
      examType: params?.examType,
      includePilot: params?.includePilot ? '1' : undefined,
    });

    return createRequest<T.DashboardStats>(`/api/admin/dashboard/stats${query}`, config);
  },
  async getDashboardHealthMatrix(params?: T.DashboardStatsQuery): Promise<T.DashboardHealthMatrix> {
    const query = buildQueryString({
      languageId: params?.languageId,
      examType: params?.examType,
    });

    return createRequest<T.DashboardHealthMatrix>(
      `/api/admin/dashboard/health-matrix${query}`,
      config
    );
  },
  async getDashboardRiskQueue(params?: T.DashboardStatsQuery): Promise<T.DashboardRiskQueue> {
    const query = buildQueryString({
      languageId: params?.languageId,
      examType: params?.examType,
    });

    return createRequest<T.DashboardRiskQueue>(`/api/admin/dashboard/risk-queue${query}`, config);
  },

  // Users
  async getUsers(params: T.PaginationParams & T.UserFilters): Promise<T.PaginatedResponse<T.User>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && query.set(k, String(v)));
    return createRequest(`/api/admin/users?${query.toString()}`, config);
  },
  async getUser(id: string): Promise<T.UserDetail> {
    return createRequest(`/api/admin/users/${id}`, config);
  },
  async getUserQuotas(id: string): Promise<T.AdminUserQuotaSnapshot> {
    return createRequest(`/api/admin/users/${id}/quotas`, config);
  },
  async updateUser(id: string, data: T.UpdateUserData): Promise<{ success: true }> {
    return createRequest(`/api/admin/users/${id}`, config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async toggleUserStatus(id: string): Promise<{ success: true; status: string }> {
    return createRequest(`/api/admin/users/${id}/toggle-status`, config, { method: 'POST' });
  },
  async deleteUser(id: string): Promise<{ success: true }> {
    return createRequest(`/api/admin/users/${id}`, config, { method: 'DELETE' });
  },

  // Questions
  async getQuestions(
    params: T.PaginationParams & T.QuestionFilters
  ): Promise<T.PaginatedResponse<T.Question>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && query.set(k, String(v)));
    return createRequest(`/api/admin/questions?${query.toString()}`, config);
  },
  async getQuestion(id: string): Promise<T.QuestionDetail> {
    return createRequest(`/api/admin/questions/${id}`, config);
  },
  async createQuestion(data: T.CreateQuestionData): Promise<T.Question> {
    return createRequest('/api/admin/questions', config, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async updateQuestion(id: string, data: Partial<T.CreateQuestionData>): Promise<T.Question> {
    return createRequest(`/api/admin/questions/${id}`, config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async deleteQuestion(id: string): Promise<{ success: true }> {
    return createRequest(`/api/admin/questions/${id}`, config, { method: 'DELETE' });
  },
  async batchDeleteQuestions(
    ids: string[],
    options?: { bankId?: string }
  ): Promise<{ success: true; deleted: number }> {
    return createRequest<{ success: true; deleted: number }>(
      '/api/admin/questions/batch-delete',
      config,
      {
        method: 'POST',
        body: JSON.stringify({
          ids,
          ...(options?.bankId ? { bankId: options.bankId } : {}),
        }),
      }
    );
  },
  async generateQuestionsWithAI(
    params: T.GenerateQuestionsWithAIParams
  ): Promise<T.GenerateQuestionsWithAIResult> {
    return createRequest('/api/admin/questions/generate-ai', config, {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 180000, // AI 生题耗时较长，单次请求超时 3 分钟
    });
  },
  // AI 任务（异步）
  async createAITask(params: T.CreateAITaskParams): Promise<{ taskId: string }> {
    return createRequest<{ taskId: string }>('/api/ai/tasks', config, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
  async getAITasks(params?: {
    status?: string;
    type?: string;
    languageId?: string;
    examType?: string;
    skillId?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: T.AITaskSummary[];
    pagination?: { page: number; limit: number; total: number; hasActive?: boolean };
  }> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.type) query.set('type', params.type);
    if (params?.languageId) query.set('languageId', params.languageId);
    if (params?.examType) query.set('examType', params.examType);
    if (params?.skillId) query.set('skillId', params.skillId);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    // 后端返回 { success, data: { list, pagination } } 或旧格式 { success, data: [] }
    const res = await createRequest<
      | {
          list: T.AITaskSummary[];
          pagination: { page: number; limit: number; total: number; hasActive?: boolean };
        }
      | T.AITaskSummary[]
    >(`/api/ai/tasks${qs ? `?${qs}` : ''}`, config);
    // 兼容旧响应格式（数组）和新格式（{ list, pagination }）
    if (Array.isArray(res)) {
      return { data: res };
    }
    return { data: Array.isArray(res.list) ? res.list : [], pagination: res.pagination };
  },
  async getAITaskStats(): Promise<T.AITaskStats> {
    return createRequest<T.AITaskStats>('/api/ai/tasks/stats', config);
  },
  async getAITask(taskId: string): Promise<T.AITask> {
    return createRequest<T.AITask>(`/api/ai/tasks/${taskId}`, config);
  },
  async retryAITask(taskId: string): Promise<{ taskId: string }> {
    return createRequest<{ taskId: string }>(`/api/ai/tasks/${taskId}/retry`, config, {
      method: 'POST',
    });
  },
  async deleteAITask(taskId: string): Promise<void> {
    await createRequest(`/api/ai/tasks/${taskId}`, config, { method: 'DELETE' });
  },
  async getBankStats(bankId: string): Promise<{
    total: number;
    max: number | null;
    byType: Array<{ type: string; count: number }>;
    bySection: Array<{ section: string; count: number }>;
    byLevel: Array<{ level: string; count: number }>;
    byTopic?: Array<{ topic: string; count: number }>;
  }> {
    return createRequest(`/api/ai/tasks/bank-stats/${bankId}`, config);
  },

  /** Content Inventory 近重复质量扫描（软信号；可选诊断参数） */
  async getBankInventoryQuality(
    bankId: string,
    params?: T.BankInventoryQualityQuery
  ): Promise<T.BankInventoryQuality> {
    const query = new URLSearchParams();
    if (params?.similarityThreshold != null) {
      query.set('similarityThreshold', String(params.similarityThreshold));
    }
    if (params?.maxQuestions != null) {
      query.set('maxQuestions', String(params.maxQuestions));
    }
    if (params?.minClusterSize != null) {
      query.set('minClusterSize', String(params.minClusterSize));
    }
    const qs = query.toString();
    return createRequest<T.BankInventoryQuality>(
      `/api/admin/banks/${bankId}/inventory-quality${qs ? `?${qs}` : ''}`,
      config
    );
  },

  /** 允许重叠 / 误报：持久化 dismiss，扫描不再打扰 */
  async dismissBankInventoryDup(
    bankId: string,
    params: T.DismissInventoryDupParams
  ): Promise<T.DismissInventoryDupResult> {
    return createRequest<T.DismissInventoryDupResult>(
      `/api/admin/banks/${bankId}/inventory-quality/dismiss`,
      config,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  },

  // AI Monitor（/api/ai/monitor/*）
  async getAIMonitorMetrics(window?: number): Promise<T.AIMonitorMetrics> {
    const query = window != null ? `?window=${window}` : '';
    return createRequest<T.AIMonitorMetrics>(`/api/ai/monitor/metrics${query}`, config);
  },
  async getAIMonitorSpans(limit?: number): Promise<{ spans: T.TraceSpan[] }> {
    const query = limit != null ? `?limit=${limit}` : '';
    return createRequest<{ spans: T.TraceSpan[] }>(`/api/ai/monitor/spans${query}`, config);
  },
  async getAIMonitorSlowQueries(
    threshold?: number,
    limit?: number
  ): Promise<{ slowQueries: T.TraceSpan[] }> {
    const params = new URLSearchParams();
    if (threshold != null) params.set('threshold', String(threshold));
    if (limit != null) params.set('limit', String(limit));
    const query = params.toString() ? `?${params.toString()}` : '';
    return createRequest<{ slowQueries: T.TraceSpan[] }>(
      `/api/ai/monitor/slow-queries${query}`,
      config
    );
  },
  async getAIMonitorErrors(limit?: number): Promise<{ errors: T.TraceSpan[] }> {
    const query = limit != null ? `?limit=${limit}` : '';
    return createRequest<{ errors: T.TraceSpan[] }>(`/api/ai/monitor/errors${query}`, config);
  },
  async getAIMonitorCacheStats(): Promise<T.CacheStats> {
    return createRequest<T.CacheStats>('/api/ai/monitor/cache-stats', config);
  },
  async getAIMonitorConfig(): Promise<T.AIServiceConfig> {
    return createRequest('/api/ai/monitor/config', config);
  },
  async updateAIProductionGates(
    data: T.UpdateAIProductionGatesInput
  ): Promise<T.AIProductionGatesUpdateResult> {
    return createRequest<T.AIProductionGatesUpdateResult>(
      '/api/ai/monitor/production-gates',
      config,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  },
  async getAIMonitorExport(): Promise<unknown> {
    return createRequest('/api/ai/monitor/export', config);
  },
  async clearAIMonitorCache(): Promise<{ message: string }> {
    return createRequest<{ message: string }>('/api/ai/monitor/clear-cache', config, {
      method: 'POST',
    });
  },

  // TTS 生成音频（单题）。language 或 examType 用于 Polly 时按题目语言合成（examType 会映射为 Polly 语言码）
  async generateTTS(
    text: string,
    options?: { voice?: string; language?: string; examType?: string }
  ): Promise<{ url: string; objectName: string }> {
    const res = await createRequest<{ url: string; objectName: string }>(
      '/api/tts/generate',
      config,
      {
        method: 'POST',
        body: JSON.stringify({
          text,
          voice: options?.voice,
          language: options?.language,
          examType: options?.examType,
        }),
      }
    );
    return res;
  },
  async importAIGeneratedQuestions(
    params: T.ImportAIGeneratedQuestionsParams
  ): Promise<T.ImportAIGeneratedQuestionsResult> {
    const res = await createRequest<T.ImportAIGeneratedQuestionsResult>(
      '/api/admin/questions/import-ai',
      config,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return res;
  },

  async discardAITaskQuestions(
    taskId: string,
    params: T.DiscardAITaskQuestionsParams
  ): Promise<T.DiscardAITaskQuestionsResult> {
    return createRequest<T.DiscardAITaskQuestionsResult>(
      `/api/ai/tasks/${encodeURIComponent(taskId)}/discard-questions`,
      config,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  },

  // Question feedback (learner → admin Review Queue input)
  async getQuestionFeedback(
    params: T.PaginationParams & T.QuestionFeedbackFilters
  ): Promise<T.PaginatedResponse<T.QuestionFeedback>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && query.set(k, String(v)));
    return createRequest(`/api/admin/question-feedback?${query.toString()}`, config);
  },
  async updateQuestionFeedbackStatus(
    id: string,
    data: { status: Exclude<T.QuestionFeedbackStatus, 'pending'> }
  ): Promise<{ feedback: T.QuestionFeedback }> {
    return createRequest(`/api/admin/question-feedback/${id}`, config, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Banks
  async getBanks(
    params: T.PaginationParams & T.BankFilters
  ): Promise<T.PaginatedResponse<T.QuestionBank>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && query.set(k, String(v)));
    return createRequest(`/api/admin/banks?${query.toString()}`, config);
  },
  async getBank(id: string): Promise<T.QuestionBank> {
    return createRequest<T.QuestionBank>(`/api/admin/banks/${id}`, config);
  },
  async createBank(data: T.CreateBankData): Promise<{ success: true; data: T.QuestionBank }> {
    return createRequest('/api/admin/banks', config, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async updateBank(
    id: string,
    data: Partial<T.CreateBankData>
  ): Promise<{ success: true; data: T.QuestionBank }> {
    return createRequest(`/api/admin/banks/${id}`, config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async deleteBank(id: string): Promise<{ success: true }> {
    return createRequest(`/api/admin/banks/${id}`, config, { method: 'DELETE' });
  },
  async toggleBankPublish(
    id: string
  ): Promise<{ isPublished: boolean; message?: string; warnings?: string[] }> {
    return createRequest(`/api/admin/banks/${id}/toggle-publish`, config, { method: 'POST' });
  },
  async toggleBankFeatured(id: string): Promise<{ isFeatured: boolean; message?: string }> {
    return createRequest(`/api/admin/banks/${id}/toggle-featured`, config, { method: 'POST' });
  },

  // Exams
  async getExams(
    params: T.PaginationParams & T.ExamFilters
  ): Promise<T.PaginatedResponse<T.ExamRecord>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && query.set(k, String(v)));
    return createRequest(`/api/admin/exams?${query.toString()}`, config);
  },
  async getExam(id: string): Promise<T.ExamDetail> {
    return createRequest(`/api/admin/exams/${id}`, config);
  },

  // Learning evidence
  async getTrainingRecords(
    params: Pick<
      T.TrainingAttemptRecordQuery,
      | 'userId'
      | 'languageId'
      | 'examType'
      | 'skillId'
      | 'contentPack'
      | 'moduleId'
      | 'intensity'
      | 'source'
      | 'sourceType'
      | 'evidenceSource'
      | 'evidenceScope'
      | 'reviewFocus'
      | 'limit'
    > = {}
  ): Promise<{ records: T.TrainingAttemptRecord[] }> {
    const query = new URLSearchParams();
    if (params.userId) query.set('userId', params.userId);
    if (params.languageId) query.set('languageId', params.languageId);
    if (params.examType) query.set('examType', params.examType);
    if (params.skillId) query.set('skillId', params.skillId);
    if (params.contentPack) query.set('contentPack', params.contentPack);
    if (params.moduleId) query.set('moduleId', params.moduleId);
    if (params.intensity) query.set('intensity', params.intensity);
    if (params.source) query.set('source', params.source);
    if (params.sourceType) query.set('sourceType', params.sourceType);
    if (params.evidenceSource) query.set('evidenceSource', params.evidenceSource);
    if (params.evidenceScope) query.set('evidenceScope', params.evidenceScope);
    if (params.reviewFocus) query.set('reviewFocus', params.reviewFocus);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return createRequest<{ records: T.TrainingAttemptRecord[] }>(
      `/api/admin/learning/training-records${qs ? `?${qs}` : ''}`,
      config
    );
  },

  // Settings
  async getSettings(): Promise<T.SystemSettings> {
    return createRequest('/api/admin/settings', config);
  },
  async updateSettings(data: Partial<T.SystemSettings>): Promise<T.SystemSettings> {
    return createRequest('/api/admin/settings', config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
});

/** Public platform settings (no auth) — learner maintenance / announcement / gates. */
export const createPlatformService = (config: ClientConfig) => ({
  async getPublicSettings(): Promise<T.PublicPlatformSettings> {
    return createRequest('/api/platform/settings', config);
  },
});

export const createQuestionService = (config: ClientConfig) => ({
  async list(params?: any) {
    const query = new URLSearchParams();
    if (params)
      Object.entries(params).forEach(([k, v]) => v !== undefined && query.set(k, String(v)));
    return createCachedRequest(`/api/questions?${query.toString()}`, config);
  },
  async get(id: string): Promise<{ question: T.Question }> {
    return createCachedRequest<{ question: T.Question }>(`/api/questions/${id}`, config);
  },
  async create(data: Omit<T.Question, 'id'>): Promise<{ question: T.Question }> {
    return createRequest('/api/questions', config, { method: 'POST', body: JSON.stringify(data) });
  },
  async update(id: string, data: Partial<T.Question>): Promise<{ question: T.Question }> {
    return createRequest(`/api/questions/${id}`, config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async delete(id: string): Promise<void> {
    return createRequest(`/api/questions/${id}`, config, { method: 'DELETE' });
  },
  async submitFeedback(
    questionId: string,
    data: { type: T.QuestionFeedbackType; description?: string }
  ): Promise<{ feedback: T.QuestionFeedback }> {
    return createRequest(`/api/questions/${questionId}/feedback`, config, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
});

export const createExamService = (config: ClientConfig) => ({
  async start(params: {
    examType: string;
    languageId?: string;
    /** Required: pre-assembled paper ids. Random start is rejected by the API. */
    questionIds: string[];
    /** @deprecated Ignored; kept for wire compat. Full mock size comes from buildPaper. */
    questionCount?: number;
  }): Promise<{ session: T.ExamSession; questions: T.Question[] }> {
    return createRequest('/api/exams/start', config, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
  async submitAnswer(
    sessionId: string,
    data: { questionId: string; answer: string | string[]; timeSpent: number }
  ): Promise<any> {
    return createRequest(`/api/exams/${sessionId}/answer`, config, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async submit(sessionId: string): Promise<any> {
    return createRequest(`/api/exams/${sessionId}/submit`, config, { method: 'POST' });
  },
  async history(params: T.ExamHistoryQuery = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.languageId) query.set('languageId', params.languageId);
    if (params.examType) query.set('examType', params.examType);

    const qs = query.toString();
    return createCachedRequest<{ sessions: T.ExamSession[]; pagination: T.Pagination }>(
      `/api/exams/history${qs ? `?${qs}` : ''}`,
      config
    );
  },
  async get(sessionId: string): Promise<{ session: T.ExamSession }> {
    return createCachedRequest<{ session: T.ExamSession }>(`/api/exams/${sessionId}`, config);
  },
});

export const createLearningService = (config: ClientConfig) => ({
  async getTrainingRecords(
    params: T.TrainingAttemptRecordQuery = {}
  ): Promise<{ records: T.TrainingAttemptRecord[] }> {
    const query = new URLSearchParams();
    if (params.languageId) query.set('languageId', params.languageId);
    if (params.examType) query.set('examType', params.examType);
    if (params.skillId) query.set('skillId', params.skillId);
    if (params.contentPack) query.set('contentPack', params.contentPack);
    if (params.moduleId) query.set('moduleId', params.moduleId);
    if (params.intensity) query.set('intensity', params.intensity);
    if (params.source) query.set('source', params.source);
    if (params.sourceType) query.set('sourceType', params.sourceType);
    if (params.evidenceSource) query.set('evidenceSource', params.evidenceSource);
    if (params.evidenceScope) query.set('evidenceScope', params.evidenceScope);
    if (params.reviewFocus) query.set('reviewFocus', params.reviewFocus);
    if (params.limit) query.set('limit', String(params.limit));

    const qs = query.toString();
    return createRequest<{ records: T.TrainingAttemptRecord[] }>(
      `/api/learning/training-records${qs ? `?${qs}` : ''}`,
      config
    );
  },

  async saveTrainingRecord(
    data: T.CreateTrainingAttemptRecordData
  ): Promise<{ record: T.TrainingAttemptRecord }> {
    return createRequest<{ record: T.TrainingAttemptRecord }>(
      '/api/learning/training-records',
      config,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  async getAbilitySnapshots(
    params: T.AbilitySnapshotQuery = {}
  ): Promise<{ snapshots: T.AbilitySnapshot[] }> {
    const query = new URLSearchParams();
    if (params.examType) query.set('examType', params.examType);
    if (params.limit) query.set('limit', String(params.limit));

    const qs = query.toString();
    return createRequest<{ snapshots: T.AbilitySnapshot[] }>(
      `/api/learning/ability-snapshots${qs ? `?${qs}` : ''}`,
      config
    );
  },

  async saveAbilitySnapshot(
    data: T.CreateAbilitySnapshotData
  ): Promise<{ snapshot: T.AbilitySnapshot }> {
    return createRequest<{ snapshot: T.AbilitySnapshot }>(
      '/api/learning/ability-snapshots',
      config,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },
});

export const createMistakeService = (config: ClientConfig) => ({
  async list(params?: any) {
    const query = new URLSearchParams();
    if (params)
      Object.entries(params).forEach(([k, v]) => v !== undefined && query.set(k, String(v)));
    return createCachedRequest(`/api/mistakes?${query.toString()}`, config);
  },
  async getDue(limit?: number): Promise<{ mistakes: T.Mistake[] }> {
    const query = limit ? `?limit=${limit}` : '';
    return createCachedRequest<{ mistakes: T.Mistake[] }>(`/api/mistakes/due${query}`, config);
  },
  async saveMistake(questionId: string, userAnswer: string): Promise<any> {
    return createRequest('/api/mistakes', config, {
      method: 'POST',
      body: JSON.stringify({ questionId, userAnswer }),
    });
  },
  async review(id: string, remembered: boolean): Promise<any> {
    return createRequest(`/api/mistakes/${id}/review`, config, {
      method: 'POST',
      body: JSON.stringify({ remembered }),
    });
  },
  async delete(id: string): Promise<void> {
    return createRequest(`/api/mistakes/${id}`, config, { method: 'DELETE' });
  },
  async stats(): Promise<T.MistakeStats> {
    return createCachedRequest<T.MistakeStats>('/api/mistakes/stats', config);
  },
});

export const createSyncService = (config: ClientConfig) => ({
  async push(deviceId: string, data: any): Promise<{ syncedAt: string }> {
    return createRequest('/api/sync/push', config, {
      method: 'POST',
      body: JSON.stringify({ deviceId, data }),
    });
  },
  async pull(deviceId: string, lastSyncTime?: number): Promise<any> {
    const query = new URLSearchParams({ deviceId });
    if (lastSyncTime) query.set('lastSyncTime', String(lastSyncTime));
    return createRequest(`/api/sync/pull?${query.toString()}`, config);
  },
  async status(deviceId?: string): Promise<any> {
    const query = deviceId ? `?deviceId=${deviceId}` : '';
    return createRequest(`/api/sync/status${query}`, config);
  },
});

export const createAIService = (config: ClientConfig) => ({
  async analyzeQuestion(questionId: string): Promise<any> {
    return createRequest('/api/ai/analyze-question', config, {
      method: 'POST',
      body: JSON.stringify({ questionId }),
    });
  },
  async answerQuestion(questionId: string, userQuestion: string): Promise<any> {
    return createRequest('/api/ai/answer', config, {
      method: 'POST',
      body: JSON.stringify({ questionId, userQuestion }),
    });
  },
  async recommendLearningPath(params: {
    currentLevel: string;
    targetLevel: string;
    examType: string;
    weakAreas?: string[];
  }): Promise<any> {
    return createRequest('/api/ai/learning-path', config, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
  async gradeWriting(params: {
    examId: string;
    skillId: string;
    prompt: string;
    essay: string;
    wordCount: number;
    taskNumber?: number | null;
    minWordCount?: number | null;
    maxWordCount?: number | null;
    languageId?: string | null;
    previousEssay?: string | null;
    previousScore?: number | null;
    /** One-shot free revision credit from a prior paid AI grade. */
    revisionCredit?: string | null;
  }): Promise<{
    grade: {
      score: number;
      scoreMax: number;
      dimensionMax: number;
      scaleLabel: string;
      levelHint: string;
      feedback: string;
      strengths: string[];
      improvements: string[];
      dimensions: Array<{
        id: string;
        label: string;
        score: number;
        action: string;
      }>;
      /** True when local word-count heuristic replaced LLM grading. */
      isHeuristic?: boolean;
    };
    /** Present after a paid non-heuristic grade — use once on revision re-grade. */
    revisionCredit?: string | null;
    quotaCharged?: boolean;
    usedRevisionCredit?: boolean;
  }> {
    return createRequest('/api/ai/writing/grade', config, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
});

export interface UploadResult {
  url: string;
  objectName: string;
}

/**
 * Service for binary uploads. Bypasses {@link createRequest} because the
 * request body is multipart/form-data, not JSON.
 *
 * The auth token, if any, is sourced through {@link ClientConfig.getAccessToken}
 * — same hook used by the rest of the SDK for OIDC tokens. There is no
 * encoding-side secret handling here; all credentials and base URL come from
 * the injected config.
 */
export const createUploadService = (config: ClientConfig) => ({
  async uploadFile(
    file: Blob,
    options: { prefix?: string; filename?: string } = {}
  ): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file, options.filename);
    if (options.prefix) formData.append('prefix', options.prefix);

    const headers: Record<string, string> = {};
    if (config.getAccessToken) {
      const token = await config.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeout = config.timeout ?? 30000;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(`${config.baseUrl}/api/upload/file`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const text = await response.text();
      const data: {
        success?: boolean;
        data?: UploadResult;
        error?: { message?: string; code?: string };
      } = text ? JSON.parse(text) : {};

      if (!response.ok || !data.success) {
        throw new ApiError(
          response.status,
          data.error?.message ?? 'Upload failed',
          data.error?.code
        );
      }
      return data.data as UploadResult;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof ApiError) throw err;
      throw new ApiError(
        0,
        err instanceof Error ? `网络错误: ${err.message}` : 'Upload failed',
        'UPLOAD_NETWORK_ERROR'
      );
    }
  },
});
